/** Data transformation utilities extracted from reports-tab.js. Pure functions. */

// Chart constants

/**
 * Mapping from internal series ids to human-readable labels shown in
 * chart tooltips (e.g. ResponsiveBar tooltip ids).
 */
export const tooltipLabelMap = {
    contributors: "Contributors",
    responses: "Responses",
    uniqueResponses: "Unique Responses",
    votes: "Matched Responses",
};

/**
 * Nivo chart theme override: bold axis legend text. Passed via the
 * `theme` prop on ResponsiveBar.
 */
export const chartTheme = {
    axis: { legend: { text: { fontWeight: "bold" } } },
};

/**
 * Base margin applied to Nivo charts. The reports tab spreads this and
 * overrides `bottom` / `left` per chart based on legend direction and
 * label widths.
 */
export const CHART_BASE_MARGIN = { top: 0, right: 20 };

/**
 * Tailwind class string for the absolutely-positioned y-axis label
 * ("Questions", "Themes", "Response options") on charts.
 */
export const CHART_Y_AXIS_LABEL_CLASS =
    "absolute top-0 left-2 text-[12px] font-bold font-[sans-serif] text-[#333] z-10";

// Numeric / formatting helpers

/**
 * Median of a numeric array. Returns 0 for null/undefined or empty input.
 * Handles odd and even length arrays. Does not mutate the input.
 *
 * @param {number[] | null | undefined} arr
 * @returns {number}
 */
export function calcMedian(arr) {
    if (!arr || !arr.length) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Calculate a left margin (in pixels) for a horizontal Nivo bar chart
 * based on the longest label that will appear on the y-axis.
 *
 * Options:
 *   - charWidth:  pixels per character (default 8)
 *   - padding:    extra pixels added to computed width (default 20)
 *   - minMargin:  floor (default 60)
 *   - maxMargin:  ceiling (default 180)
 *
 * @param {string[]} labels
 * @param {{ charWidth?: number, padding?: number, minMargin?: number, maxMargin?: number }} [options]
 * @returns {number}
 */
export const calculateLeftMargin = (labels, options = {}) => {
    const { charWidth = 8, padding = 20, minMargin = 60, maxMargin = 180 } = options;
    if (!labels || labels.length === 0) return minMargin;
    let maxLabelLength = 0;
    labels.forEach((label) => {
        if (typeof label === "string" && label.length > maxLabelLength)
            maxLabelLength = label.length;
    });
    return Math.max(
        Math.min(maxLabelLength * charWidth + padding, maxMargin),
        minMargin,
    );
};

/**
 * Truncate a string label to `maxLength` characters, appending "..." if
 * truncation occurred. Non-string input is returned unchanged so callers
 * can pipe possibly-null values through without extra guards.
 *
 * @param {string | null | undefined} label
 * @param {number} [maxLength=20]
 * @returns {string | null | undefined}
 */
export const truncateLabel = (label, maxLength = 20) => {
    if (!label || typeof label !== "string") return label;
    return label.length > maxLength ? `${label.substring(0, maxLength)}...` : label;
};

/**
 * Render a boolean as "Yes" / "No". Falsy values (false, 0, "", null,
 * undefined) all return "No"; truthy values return "Yes".
 *
 * @param {any} value
 * @returns {"Yes" | "No"}
 */
export const formatBoolean = (value) => (value ? "Yes" : "No");

/**
 * Format a decimal (0..1) as a percentage string with up to 2 decimal
 * places. Non-finite input (NaN, Infinity, null, undefined, strings)
 * returns "N/A".
 *
 * @param {number | null | undefined} decimal
 * @returns {string}
 */
export const percentFormat = (decimal) => {
    if (!Number.isFinite(decimal)) return "N/A";
    return Intl.NumberFormat("default", {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(decimal);
};

// Sentiment helpers

/**
 * Flatten or filter the sentiment-by-keyphrase response payload.
 *
 * The API returns an object keyed by sentiment label (e.g. "positive",
 * "neutral", "negative"). Each value is an array of keyphrase entries.
 *
 * - When `chosenSentiment` is provided (e.g. "positive"), returns the
 *   array for that key only.
 * - When `chosenSentiment` is falsy (null / "" / undefined), merges all
 *   arrays into a single list and tags each entry with
 *   `overall_sentiment` so downstream UI can colour-code rows.
 *
 * @param {Record<string, Array<{ text: string }>>} sentimentList
 * @param {string | null | undefined} chosenSentiment
 * @returns {Array<{ text: string, overall_sentiment?: string }> | undefined}
 */
export const sentimentByKeyPhraseFiltered = (sentimentList, chosenSentiment) => {
    if (chosenSentiment) {
        return sentimentList?.[chosenSentiment];
    }
    if (!sentimentList) return [];
    const sentimentArray = [];
    Object.keys(sentimentList).forEach((key) => {
        const keyphrases = sentimentList[key];
        const keyphrasesSentimented = keyphrases.map((k) => ({
            ...k,
            overall_sentiment: key,
        }));
        sentimentArray.push(...keyphrasesSentimented);
    });
    return sentimentArray;
};

/**
 * Format a min-max range of scores across a list of responses for a
 * given score field (e.g. "keyphrase_score").
 *
 * Behaviour:
 *   - If min != max and both are truthy, returns "min — max" (the min
 *     percent is stripped of its trailing "%" so the range reads
 *     "12 — 45%" rather than "12% — 45%").
 *   - If only max is truthy, returns just the max percent.
 *   - If only min is truthy, returns just the min percent.
 *   - Otherwise returns "N/A".
 *
 * @param {Array<Record<string, number>>} responses
 * @param {string} score_type
 * @returns {string}
 */
export const sentimentRangeFormat = (responses, score_type) => {
    if (!responses || responses.length === 0) return "N/A";
    const max = responses.reduce((prev, current) =>
        prev && prev[score_type] > current[score_type] ? prev : current,
    );
    const min = responses.reduce((prev, current) =>
        prev && prev[score_type] < current[score_type] ? prev : current,
    );
    let string = "N/A";
    if (
        max[score_type] &&
        min[score_type] &&
        min[score_type] !== max[score_type]
    ) {
        string = `${percentFormat(min[score_type]).slice(0, -1)} — ${percentFormat(max[score_type])}`;
    } else if (max) {
        string = `${percentFormat(max[score_type])}`;
    } else if (min) {
        string = `${percentFormat(min[score_type])}`;
    }
    return string;
};
